// ===================================
// ADMIN SETTINGS PAGE - STAGE 7
// src/pages/AdminSettings.js
// Platform configuration settings
// ===================================

import React, { useState, useEffect } from 'react';
import { getSettings, updateSetting } from '../services/adminApi';
import './AdminSettings.css';

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Default settings structure
  const defaultSettings = {
    pricing_15_text: '9.99',
    pricing_30_text: '14.99',
    pricing_60_text: '24.99',
    pricing_15_voice: '14.99',
    pricing_30_voice: '24.99',
    pricing_60_voice: '39.99',
    pricing_15_phone: '19.99',
    pricing_30_phone: '34.99',
    pricing_60_phone: '49.99',
    max_retry_attempts: '3',
    session_expiry_days: '7',
    enable_phone_interviews: 'true',
    enable_voice_interviews: 'true',
    maintenance_mode: 'false'
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      setSettings({ ...defaultSettings, ...response.settings });
    } catch (err) {
      console.error('Error loading settings:', err);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSuccess(null);
  };

  const handleSave = async (key) => {
    try {
      setSaving(true);
      setError(null);
      await updateSetting(key, settings[key]);
      setSuccess(`Setting "${key}" saved successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      setError(null);
      for (const [key, value] of Object.entries(settings)) {
        await updateSetting(key, value);
      }
      setSuccess('All settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-settings-page">
        <div className="loading-container"><div className="spinner"></div><p>Loading settings...</p></div>
      </div>
    );
  }

  return (
    <div className="admin-settings-page">
      <div className="page-header">
        <div className="header-content">
          <h1>⚙️ Platform Settings</h1>
          <p>Configure platform behavior and pricing</p>
        </div>
        <button onClick={handleSaveAll} className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : '💾 Save All Settings'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="settings-grid">
        {/* Pricing - Text Interviews */}
        <div className="settings-card">
          <div className="card-header"><h3>💬 Text Interview Pricing</h3></div>
          <div className="card-body">
            <div className="setting-row">
              <label>15 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_15_text} onChange={(e) => handleChange('pricing_15_text', e.target.value)} />
              </div>
            </div>
            <div className="setting-row">
              <label>30 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_30_text} onChange={(e) => handleChange('pricing_30_text', e.target.value)} />
              </div>
            </div>
            <div className="setting-row">
              <label>60 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_60_text} onChange={(e) => handleChange('pricing_60_text', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* Pricing - Voice Interviews */}
        <div className="settings-card">
          <div className="card-header"><h3>🎙️ Voice Interview Pricing</h3></div>
          <div className="card-body">
            <div className="setting-row">
              <label>15 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_15_voice} onChange={(e) => handleChange('pricing_15_voice', e.target.value)} />
              </div>
            </div>
            <div className="setting-row">
              <label>30 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_30_voice} onChange={(e) => handleChange('pricing_30_voice', e.target.value)} />
              </div>
            </div>
            <div className="setting-row">
              <label>60 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_60_voice} onChange={(e) => handleChange('pricing_60_voice', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* Pricing - Phone Interviews */}
        <div className="settings-card">
          <div className="card-header"><h3>📞 Phone Interview Pricing</h3></div>
          <div className="card-body">
            <div className="setting-row">
              <label>15 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_15_phone} onChange={(e) => handleChange('pricing_15_phone', e.target.value)} />
              </div>
            </div>
            <div className="setting-row">
              <label>30 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_30_phone} onChange={(e) => handleChange('pricing_30_phone', e.target.value)} />
              </div>
            </div>
            <div className="setting-row">
              <label>60 Minutes</label>
              <div className="input-group">
                <span className="input-prefix">$</span>
                <input type="number" step="0.01" value={settings.pricing_60_phone} onChange={(e) => handleChange('pricing_60_phone', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* Session Settings */}
        <div className="settings-card">
          <div className="card-header"><h3>🎯 Session Settings</h3></div>
          <div className="card-body">
            <div className="setting-row">
              <label>Max Retry Attempts</label>
              <input type="number" min="1" max="10" value={settings.max_retry_attempts} onChange={(e) => handleChange('max_retry_attempts', e.target.value)} />
            </div>
            <div className="setting-row">
              <label>Session Expiry (Days)</label>
              <input type="number" min="1" max="30" value={settings.session_expiry_days} onChange={(e) => handleChange('session_expiry_days', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="settings-card">
          <div className="card-header"><h3>🔧 Feature Toggles</h3></div>
          <div className="card-body">
            <div className="setting-row toggle">
              <label>Enable Voice Interviews</label>
              <label className="switch">
                <input type="checkbox" checked={settings.enable_voice_interviews === 'true'} onChange={(e) => handleChange('enable_voice_interviews', e.target.checked ? 'true' : 'false')} />
                <span className="slider"></span>
              </label>
            </div>
            <div className="setting-row toggle">
              <label>Enable Phone Interviews</label>
              <label className="switch">
                <input type="checkbox" checked={settings.enable_phone_interviews === 'true'} onChange={(e) => handleChange('enable_phone_interviews', e.target.checked ? 'true' : 'false')} />
                <span className="slider"></span>
              </label>
            </div>
            <div className="setting-row toggle warning">
              <label>Maintenance Mode</label>
              <label className="switch">
                <input type="checkbox" checked={settings.maintenance_mode === 'true'} onChange={(e) => handleChange('maintenance_mode', e.target.checked ? 'true' : 'false')} />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
